<template>
    <div class="sm_ticketBox notranslate">
        <div class="sm_ticket">
            <div class="sm_matchInfo">
                <span v-if="isShowLiveScore" class="ticket-live-score">
                    [{{ subBet.odds.eventResult.liveHomeScore }}:{{ subBet.odds.eventResult.liveAwayScore }}]
                </span>
                <span class="ticket-team">
                    {{ homeTeam }}
                </span>
                <span class="ticket-team-separation">v</span>
                <span class="ticket-team">
                    {{ awayTeam }}
                </span>
            </div>

            <div class="sm_oddInfo">
                <span class="sm_marketType">
                    {{ subBet.odds && marketGroupName }} {{ i18n.t(subBet.marketDisplayTypeName) }}
                    <SvgIcon v-if="isDisplayCashOutIcon" iconName="sportIcon_cashOut" />
                </span>
                <span class="dec_line">&nbsp;|&nbsp;</span>
                <span class="sm_oddsType">
                    {{ option }}
                </span>
                <div class="sm_oddsPoint">
                    {{ toFixedDecimalPoint(point, 2) }}
                </div>
            </div>
        </div>
        <div class="sm_value">
            <div class="sm_oddsValue" v-bind:class="{ _negative: isOddsNegative }">
                @{{ displayPrice }}
            </div>

            <div
                class="sm_btn_close MiniParlaySlip_Btn_DeleteTicket"
                v-on:click="clickDelete"
            >
                <svg
                    id="systemIcon_close2"
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                >
                    <g
                        id="ico_close-2"
                        data-name="ico_close"
                        opacity="0.4"
                    >
                        <path
                            id="ico_close-3"
                            data-name="ico_close"
                            d="M15,8a7,7,0,1,0,7,7A7,7,0,0,0,15,8Zm3.432,8.837a.339.339,0,0,1,0,.48l-1.118,1.115a.339.339,0,0,1-.48,0L15,16.581l-1.838,1.852a.339.339,0,0,1-.48,0l-1.115-1.118a.339.339,0,0,1,0-.48L13.419,15l-1.852-1.838a.339.339,0,0,1,0-.48l1.118-1.118a.339.339,0,0,1,.48,0L15,13.419l1.837-1.852a.339.339,0,0,1,.48,0l1.118,1.118a.339.339,0,0,1,0,.48L16.581,15Z"
                            transform="translate(-8 -8)"
                            fill="#7c7c7c"
                        />
                    </g>
                </svg>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
    import { storeToRefs } from 'pinia';
    import type { PropType } from 'vue';
    import { computed, defineComponent, ref, toRefs } from 'vue';
    import { DisplayMode } from '@/interface/enum';
    import { useParlayCashOut } from '@/libraries/composables/useBetSlipCashOut';
    import { useEuroTicketDisplay } from '@/libraries/composables/useEuroTicketDisplay';
    import type { IEuroTicket } from '@/libraries/euroTicket';
    import { i18n } from '@/libraries/i18n';
    import { useOddsSubscriptionCallback } from '@/libraries/oddsApi/composables/useOddsSubscriptionCallback';
    import { toFixedDecimalPoint } from '@/libraries/utils/text';
    import { useAccountStore } from '@/stores/accountStore';
    import { useParlayBetSlipStore } from '@/stores/betSlip/parlayBetSlipStore';
    import { useHeaderDisplayStore } from '@/stores/headerDisplayStore';
    import SvgIcon from '@/svg/SvgIcon.vue';

    export default defineComponent({
        name: 'MediumParlayTicket',
        components: {
            SvgIcon,
        },
        emits: ['refreshTicket'],
        props: {
            subBet: {
                type: Object as PropType<IEuroTicket>,
                default: () => ({}),
            },
        },
        setup(props, context) {
            const { removeTicketOrBetSlip } = useParlayBetSlipStore();

            const { subBet } = toRefs(props);

            const {
                homeTeam, awayTeam, marketGroupName, option, point, isOddsNegative, displayPrice, isShowLiveScore,
            } = useEuroTicketDisplay(subBet);

            const { customerInfo } = storeToRefs(useAccountStore());

            const betSlipCashOut = computed(() => ({
                sportType: subBet.value.sportType,
                isMainMarket: subBet.value.odds.marketGroup.id === 0,
                isLive: subBet.value.isLive,
                leagueId: subBet.value.leagueId,
                marketType: subBet.value.marketType,
            }));
            const { isDisplayCashOutIcon } = useParlayCashOut(computed(() => [betSlipCashOut.value]));

            const { setDisplayMode } = useHeaderDisplayStore();
            async function clickDelete() {
                await removeTicketOrBetSlip(`${subBet.value.oddsId}-${subBet.value.option}`);
                setDisplayMode(DisplayMode.ShowAll);
            }

            function handleUpdate() {
                context.emit('refreshTicket');
            }

            function handleDelete(oddsId: number) {
                if (subBet.value.oddsId === oddsId) {
                    context.emit('refreshTicket');
                }
            }

            const {
                onUpdated,
                onDeleted,
            } = useOddsSubscriptionCallback({
                eventId: subBet.value.eventId,
                oddsId: subBet.value.oddsId,
                isLive: subBet.value.isLive,
                isMixParlay: true,
                priceStyle: customerInfo.value.priceStyle,
                isActive: ref(true),
                sportType: subBet.value.sportType,
                leagueSize: subBet.value.event.leagueSize,
            });
            onUpdated(handleUpdate);
            onDeleted(handleDelete);

            return {
                i18n,
                homeTeam,
                awayTeam,
                option,
                marketGroupName,
                toFixedDecimalPoint,
                isShowLiveScore,
                isDisplayCashOutIcon,
                point,
                displayPrice,
                isOddsNegative,
                clickDelete,
            };
        },
    });
</script>
